/**
 * PractitionerCredentialsForm Component
 *
 * Form for capturing and validating medical practitioner credentials
 * Required when assigning provider role to users
 */

import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export interface PractitionerCredentials {
  medicalLicense: string;
  specialty?: string;
  deaNumber?: string;
  licenseExpiration?: string;
}

interface PractitionerCredentialsFormProps {
  credentials?: PractitionerCredentials;
  onChange: (credentials: PractitionerCredentials) => void;
  onValidation?: (isValid: boolean, errors: string[]) => void;
}

export const PractitionerCredentialsForm: React.FC<PractitionerCredentialsFormProps> = ({
  credentials,
  onChange,
  onValidation
}) => {
  const [localCredentials, setLocalCredentials] = useState<PractitionerCredentials>(
    credentials || {
      medicalLicense: '',
      specialty: '',
      deaNumber: '',
      licenseExpiration: ''
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setLocalCredentials(credentials || { medicalLicense: '', specialty: '', deaNumber: '', licenseExpiration: '' });
    setErrors({});
    setTouched({});
  }, [credentials]);

  // Validate medical license format (alphanumeric, 6-20 characters)
  const validateMedicalLicense = (license: string): string | null => {
    if (!license || license.trim().length === 0) {
      return 'National Provider Identifier (NPI) is required for provider role';
    }
    if (license.length < 6) {
      return 'National Provider Identifier (NPI) must be at least 6 characters';
    }
    if (license.length > 20) {
      return 'National Provider Identifier (NPI) must be at most 20 characters';
    }
    if (!/^[A-Za-z0-9]+$/.test(license)) {
      return 'National Provider Identifier (NPI) must be alphanumeric only';
    }
    return null;
  };

  // Validate DEA number format (2 letters + 7 digits)
  const validateDEANumber = (dea: string): string | null => {
    if (!dea || dea.trim().length === 0) {
      return null; // DEA is optional
    }
    if (!/^[A-Z]{2}\d{7}$/.test(dea)) {
      return 'DEA number must be 2 letters followed by 7 digits (e.g., AB1234563)';
    }
    return null;
  };

  // Validate license expiration
  const validateLicenseExpiration = (expiration: string): string | null => {
    if (!expiration) {
      return null; // Optional
    }

    const expirationDate = new Date(expiration);
    const today = new Date();
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

    if (expirationDate < today) {
      return 'Medical license has expired';
    }

    // Warning, not error
    if (expirationDate < threeMonthsFromNow) {
      return null; // We'll show a warning separately
    }

    return null;
  };

  const handleFieldChange = (field: keyof PractitionerCredentials, value: string) => {
    const updated = { ...localCredentials, [field]: value };
    setLocalCredentials(updated);
    onChange(updated);

    // Mark field as touched
    setTouched({ ...touched, [field]: true });

    // Validate field
    const newErrors = { ...errors };

    if (field === 'medicalLicense') {
      const error = validateMedicalLicense(value);
      if (error) {
        newErrors.medicalLicense = error;
      } else {
        delete newErrors.medicalLicense;
      }
    }

    if (field === 'deaNumber') {
      const error = validateDEANumber(value);
      if (error) {
        newErrors.deaNumber = error;
      } else {
        delete newErrors.deaNumber;
      }
    }

    if (field === 'licenseExpiration') {
      const error = validateLicenseExpiration(value);
      if (error) {
        newErrors.licenseExpiration = error;
      } else {
        delete newErrors.licenseExpiration;
      }
    }

    setErrors(newErrors);

    // Notify parent of validation status
    if (onValidation) {
      const isValid = Object.keys(newErrors).length === 0 && updated.medicalLicense.length >= 6;
      onValidation(isValid, Object.values(newErrors));
    }
  };

  const handleBlur = (field: keyof PractitionerCredentials) => {
    setTouched({ ...touched, [field]: true });
  };

  // Check if license is expiring soon
  const isExpiringSoon = () => {
    if (!localCredentials.licenseExpiration) return false;
    const expirationDate = new Date(localCredentials.licenseExpiration);
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return expirationDate < threeMonthsFromNow && expirationDate > new Date();
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start space-x-3">
        <ShieldCheckIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-blue-900">
            Practitioner Credentials Required
          </h3>
          <p className="mt-1 text-xs text-blue-700">
            Provider role requires valid medical practitioner credentials for clinical operations.
          </p>
        </div>
      </div>

      {/* Medical License */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          National Provider Identifier (NPI) <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={localCredentials.medicalLicense}
          onChange={(e) => handleFieldChange('medicalLicense', e.target.value.toUpperCase())}
          onBlur={() => handleBlur('medicalLicense')}
          className={`
            w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2
            ${errors.medicalLicense && touched.medicalLicense
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }
          `}
          placeholder="e.g., MD123456"
          required
        />
        {errors.medicalLicense && touched.medicalLicense && (
          <p className="mt-1 text-xs text-red-600">{errors.medicalLicense}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Alphanumeric, 6-20 characters
        </p>
      </div>

      {/* Specialty */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Medical Specialty
        </label>
        <input
          type="text"
          value={localCredentials.specialty || ''}
          onChange={(e) => handleFieldChange('specialty', e.target.value)}
          onBlur={() => handleBlur('specialty')}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., Family Medicine, Cardiology"
        />
        <p className="mt-1 text-xs text-gray-500">
          Recommended but not required
        </p>
      </div>

      {/* DEA Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          DEA Number (Optional)
        </label>
        <input
          type="text"
          value={localCredentials.deaNumber || ''}
          onChange={(e) => handleFieldChange('deaNumber', e.target.value.toUpperCase())}
          onBlur={() => handleBlur('deaNumber')}
          className={`
            w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2
            ${errors.deaNumber && touched.deaNumber
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }
          `}
          placeholder="e.g., AB1234563"
        />
        {errors.deaNumber && touched.deaNumber && (
          <p className="mt-1 text-xs text-red-600">{errors.deaNumber}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Required for prescribing privileges (2 letters + 7 digits)
        </p>
      </div>

      {/* License Expiration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          License Expiration Date
        </label>
        <input
          type="date"
          value={localCredentials.licenseExpiration || ''}
          onChange={(e) => handleFieldChange('licenseExpiration', e.target.value)}
          onBlur={() => handleBlur('licenseExpiration')}
          className={`
            w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2
            ${errors.licenseExpiration && touched.licenseExpiration
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }
          `}
        />
        {errors.licenseExpiration && touched.licenseExpiration && (
          <p className="mt-1 text-xs text-red-600">{errors.licenseExpiration}</p>
        )}
        {isExpiringSoon() && !errors.licenseExpiration && (
          <div className="mt-2 flex items-start space-x-2">
            <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-700">
              License will expire within 3 months. Please ensure renewal is in progress.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PractitionerCredentialsForm;
